package at.theatervogelweide.admin.web.screens.accounts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.accounts.AccountDraft
import at.theatervogelweide.admin.contract.accounts.AccountEditor
import at.theatervogelweide.admin.contract.accounts.AccountListEntry
import at.theatervogelweide.admin.contract.accounts.AccountRole
import at.theatervogelweide.admin.contract.accounts.AccountsService
import at.theatervogelweide.admin.contract.accounts.PasswordLink
import at.theatervogelweide.admin.contract.people.PeopleService
import at.theatervogelweide.admin.contract.people.PersonListEntry
import at.theatervogelweide.admin.web.components.AdminBadge
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCallout
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminCardFooterSpacer
import at.theatervogelweide.admin.web.components.AdminConfirmDialog
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminDataTable
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminPersonPicker
import at.theatervogelweide.admin.web.components.AdminSplitView
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.BadgeVariant
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.DataTableColumn
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.LocalPanes
import at.theatervogelweide.admin.web.components.SplitController
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.components.editorShown
import at.theatervogelweide.admin.web.rpcCall
import at.theatervogelweide.admin.web.screens.acts.formatDateTime
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.readOnly
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val stack = Bem("admin-stack")
private val field = Bem("admin-field")
private val input = Bem("admin-input")
private val card = Bem("admin-card")
private val listPanel = Bem("admin-list-panel")
private val alert = Bem("admin-alert")
private val fieldset = Bem("admin-fieldset")
private val dataTable = Bem("admin-data-table")
private val copyField = Bem("admin-copy-field")

/** The editor of one account, or of a new one while [loaded] is `null`. */
private class AccountEditorState(loaded: AccountEditor?) {
    var loaded: AccountEditor? by mutableStateOf(loaded)
    val original: AccountDraft = loaded?.draft ?: AccountDraft(email = "", role = AccountRole.ADMIN)
    var form: AccountDraft by mutableStateOf(original)
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)

    /** Shown once, right after it was created; leaving the account forgets it. */
    var link: PasswordLink? by mutableStateOf(null)

    /** A link is being created: the server already ended the old ones, so leaving now would lose the new one. */
    var creatingLink: Boolean by mutableStateOf(false)

    val isNew: Boolean get() = loaded == null
    val hasChanges: Boolean get() = form != original

    fun errorFor(name: String): String? = fieldErrors.firstOrNull { it.field == name }?.message

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict || failure.fieldErrors.isNotEmpty() -> null
            failure.code == ErrorCode.NOT_FOUND -> "Dieses Konto gibt es nicht mehr. Bitte wähle ein anderes."
            else -> failure.message
        }
    }
}

/**
 * Konten (#66), for superusers: the accounts beside the editor of the selected one, with its one-time link. [selectedId]
 * comes from the address, and [split] changes it; with panes and no selection the first account opens. [startNew]
 * opens the form for a new account (`/konten/neu`).
 */
@Composable
fun AccountsScreen(
    rpc: RpcClient,
    guard: UnsavedChangesGuard,
    split: SplitController,
    selectedId: Long?,
    startNew: Boolean,
    /** The signed-in superuser's own account was saved, e.g. with another e-mail address the shell shows. */
    onOwnAccountChanged: () -> Unit,
) {
    val service = remember(rpc) { rpc.withService<AccountsService>() }
    val peopleService = remember(rpc) { rpc.withService<PeopleService>() }
    val scope = rememberCoroutineScope()

    var entries by remember { mutableStateOf<List<AccountListEntry>?>(null) }
    var people by remember { mutableStateOf<List<PersonListEntry>?>(null) }
    var listError by remember { mutableStateOf<String?>(null) }
    var editor by remember { mutableStateOf<AccountEditorState?>(null) }
    var editorError by remember { mutableStateOf<String?>(null) }
    var creating by remember { mutableStateOf(startNew) }
    // the account each question is about: it only applies while that account is still the open one
    var confirmDelete by remember { mutableStateOf<AccountEditor?>(null) }
    var confirmDisable by remember { mutableStateOf<AccountEditor?>(null) }

    suspend fun reloadList() {
        when (val result = rpcCall { service.list() }) {
            is AdminResult.Success -> {
                entries = result.value
                listError = null
            }

            is AdminResult.Failure -> listError = result.error.message
        }
    }

    suspend fun loadEditor(id: Long, replacing: AccountEditorState? = editor) {
        val result = rpcCall { service.load(id) }
        if (editor !== replacing) {
            return
        }
        when (result) {
            is AdminResult.Success -> {
                editor = AccountEditorState(result.value)
                editorError = null
            }

            is AdminResult.Failure -> {
                editor = null
                editorError = if (result.error.code == ErrorCode.NOT_FOUND) "Dieses Konto gibt es nicht mehr." else result.error.message
            }
        }
    }

    LaunchedEffect(service) { reloadList() }
    var peopleError by remember { mutableStateOf<String?>(null) }
    var peopleAttempt by remember { mutableStateOf(0) }
    LaunchedEffect(peopleService, peopleAttempt) {
        when (val result = rpcCall { peopleService.list() }) {
            is AdminResult.Success -> {
                people = result.value
                peopleError = null
            }

            // not an empty list: the picker says so and offers to try again
            is AdminResult.Failure -> peopleError = result.error.message
        }
    }

    LaunchedEffect(startNew) {
        when {
            startNew && editor?.isNew != true -> {
                creating = true
                editorError = null
                editor = AccountEditorState(null)
            }

            !startNew && creating && selectedId == null -> {
                editor = null
                creating = false
            }
        }
    }

    LaunchedEffect(service, selectedId) {
        if (selectedId != null && editor?.loaded?.id != selectedId) {
            creating = false
            // the guard let this selection through; the previous account's form goes now, not after the answer
            editor = null
            loadEditor(selectedId, replacing = null)
        }
    }

    // with panes and without a selection, the first account opens
    val panes = LocalPanes.current
    LaunchedEffect(entries, selectedId, creating, panes) {
        val first = entries?.firstOrNull()
        if (panes && selectedId == null && !creating && first != null) {
            split.show(first.id)
        }
    }

    // back on the list alone, the editor, its changes and a link not copied yet are gone
    val editorVisible = editorShown(selectedId, startNew)
    LaunchedEffect(editorVisible) {
        if (!editorVisible) {
            editor = null
            editorError = null
            creating = false
        }
    }

    val state = editor.takeIf { editorVisible }
    // a link not copied yet, or one still being created, counts as unsaved: it can't be shown again
    val hasChanges = state?.hasChanges == true || state?.link != null || state?.creatingLink == true
    ReportUnsavedChanges(guard, hasChanges)

    fun select(id: Long) {
        if (id == selectedId && !creating) {
            return
        }
        guard.proceed {
            creating = false
            if (editor?.loaded?.id != id) {
                editor = null
            }
            split.open(id)
        }
    }

    fun startNewAccount() {
        guard.proceed {
            creating = true
            editorError = null
            editor = AccountEditorState(null)
            split.openNew()
        }
    }

    fun save(draft: AccountDraft) {
        val current = editor ?: return
        val errors = draft.validate()
        current.fieldErrors = errors
        current.error = null
        if (errors.isNotEmpty() || current.saving) {
            return
        }

        current.saving = true
        scope.launch {
            val loaded = current.loaded
            if (loaded == null) {
                val result = rpcCall { service.create(draft) }
                current.saving = false
                when (result) {
                    is AdminResult.Success -> {
                        val next = AccountEditorState(result.value.account).apply { link = result.value.link }
                        if (editor === current) {
                            editor = next
                            creating = false
                            split.show(result.value.account.id)
                        }
                        reloadList()
                    }

                    is AdminResult.Failure -> current.showFailure(result.error)
                }
            } else {
                val result = rpcCall { service.save(VersionedId(loaded.id, loaded.version), draft) }
                current.saving = false
                when (result) {
                    is AdminResult.Success -> {
                        if (editor === current) {
                            // a link still shown stays, as it can't be shown again, unless disabling just revoked it
                            editor = AccountEditorState(result.value).apply { link = current.link?.takeIf { it.isOpenFor(result.value) } }
                        }
                        if (result.value.self) {
                            onOwnAccountChanged()
                        }
                        reloadList()
                    }

                    is AdminResult.Failure -> current.showFailure(result.error)
                }
            }
        }
    }

    fun createLink() {
        val current = editor ?: return
        val loaded = current.loaded ?: return
        current.saving = true
        current.creatingLink = true
        scope.launch {
            val result = rpcCall { service.createLink(loaded.id) }
            current.saving = false
            current.creatingLink = false
            when (result) {
                // the account as it is now, which someone else may have changed; there are no unsaved changes to keep
                is AdminResult.Success -> if (editor === current) {
                    editor = AccountEditorState(result.value.account).apply { link = result.value.link }
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun delete() {
        val target = confirmDelete ?: return
        confirmDelete = null
        val current = editor ?: return
        val loaded = current.loaded?.takeIf { it.id == target.id } ?: return
        // the form stays locked until the answer, so nothing typed meanwhile gets lost with it
        current.saving = true
        scope.launch {
            val result = rpcCall { service.delete(VersionedId(loaded.id, loaded.version)) }
            current.saving = false
            when (result) {
                is AdminResult.Success -> {
                    entries = entries?.filterNot { it.id == loaded.id }
                    // another account opened meanwhile stays open
                    if (editor === current) {
                        editor = null
                        split.backToList()
                    }
                    reloadList()
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        val id = current.loaded?.id ?: return
        val link = current.link
        scope.launch {
            loadEditor(id, replacing = current)
            // a link still shown stays, as it can't be shown again, unless the account was disabled meanwhile
            editor?.takeIf { reloaded -> reloaded.loaded?.let { link?.isOpenFor(it) } == true }?.link = link
            reloadList()
        }
    }

    AdminPageHeader("Konten", unsavedChanges = state?.hasChanges == true) {
        AdminButton(variant = ButtonVariant.PRIMARY, onClick = ::startNewAccount) { Text("+ Konto") }
    }

    AdminSplitView(
        controller = split,
        selectedId = selectedId,
        new = startNew,
        name = "Konten",
        listLabel = "Liste der Konten",
        table = true,
        detailClasses = listOf(stack.block),
        list = {
            listError?.let {
                P({
                    classes(listPanel.element("message").className, alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            val all = entries
            when {
                all == null -> if (listError == null) P({ classes(listPanel.element("message").className) }) { Text("Lade …") }
                else -> AdminDataTable(
                    rows = all,
                    key = { it.id },
                    caption = "Konten",
                    selected = if (creating) null else selectedId,
                    recent = split.recent,
                    inCard = true,
                    columns = listOf(
                        DataTableColumn("E-Mail") { account ->
                            Button({
                                type(ButtonType.Button)
                                classes(dataTable.element("select").className)
                                attr("aria-pressed", (!creating && account.id == selectedId).toString())
                                onClick { select(account.id) }
                            }) {
                                Span({ classes(dataTable.element("text").className) }) {
                                    Span({ classes(dataTable.element("title").className) }) {
                                        Text(account.email)
                                        if (account.self) {
                                            Text(" ")
                                            Span({ classes(dataTable.element("note").className) }) { Text("(du)") }
                                        }
                                    }
                                    account.personName?.let { Span({ classes(dataTable.element("meta").className) }) { Text(it) } }
                                }
                            }
                            },
                        DataTableColumn("Rolle") { account -> Text(account.role?.label ?: "Kein Zugang") },
                        DataTableColumn("Status") { account -> StatusBadge(account.enabled, account.passwordSet) },
                    ),
                )
            }
        },
        detail = {
            when {
                // a new editor gets new fields, without the search typed into the previous one
                state != null -> key(state) {
                    AccountEditorCard(
                        state = state,
                        people = people,
                        peopleError = peopleError,
                        onRetryPeople = { peopleAttempt++ },
                        onSave = { save(state.form) },
                        onDiscard = {
                            if (state.isNew) {
                                editor = null
                                creating = false
                                split.backToList()
                            } else {
                                state.form = state.original
                                state.fieldErrors = emptyList()
                                state.error = null
                            }
                            },
                        onDelete = { confirmDelete = state.loaded },
                        onReload = ::reloadAfterConflict,
                    )
                    state.loaded?.let { loaded ->
                        SignInCard(
                            state = state,
                            loaded = loaded,
                            onCreateLink = ::createLink,
                            onLinkDone = { state.link = null },
                            onToggleEnabled = {
                                if (loaded.draft.enabled) confirmDisable = loaded else save(loaded.draft.copy(enabled = true))
                                },
                        )
                    }
                }

                editorError != null -> P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(editorError!!) }
            }
        },
    )

    val openId = state?.loaded?.id
    val toDelete = confirmDelete?.takeIf { it.id == openId }
    AdminConfirmDialog(
        open = toDelete != null,
        id = "konto-loeschen",
        title = "Konto löschen?",
        message = buildString {
            append("Das Konto „${toDelete?.draft?.email.orEmpty()}“ verschwindet mit seinen offenen Links und Verbindungen zu Anwendungen.")
            val news = toDelete?.newsCount ?: 0
            if (news > 0) {
                append(if (news == 1) " Die Neuigkeit unter diesem Namen bleibt," else " Die $news Neuigkeiten unter diesem Namen bleiben,")
                append(" aber ohne Namen.")
            }
            append(" Das lässt sich nicht rückgängig machen.")
        },
        confirmLabel = "Löschen",
        cancelLabel = "Behalten",
        onConfirm = ::delete,
        onCancel = { confirmDelete = null },
        danger = true,
    )
    val toDisable = confirmDisable?.takeIf { it.id == openId }
    AdminConfirmDialog(
        open = toDisable != null,
        id = "konto-deaktivieren",
        title = "Konto deaktivieren?",
        message = "„${toDisable?.draft?.email.orEmpty()}“ wird sofort abgemeldet, seine Verbindungen zu Anwendungen enden, und offene " +
            "Links gelten nicht mehr. Aktivieren geht jederzeit wieder.",
        confirmLabel = "Deaktivieren",
        cancelLabel = "Aktiv lassen",
        onConfirm = {
            confirmDisable = null
            // only for the account the question was about
            toDisable?.takeIf { it.id == editor?.loaded?.id }?.let { save(it.draft.copy(enabled = false)) }
        },
        onCancel = { confirmDisable = null },
        danger = true,
    )
}

@Composable
private fun StatusBadge(enabled: Boolean, passwordSet: Boolean) {
    when {
        !enabled -> AdminBadge("Deaktiviert", BadgeVariant.INACTIVE)
        !passwordSet -> AdminBadge("Wartet auf Passwort", BadgeVariant.PENDING)
        else -> AdminBadge("Aktiv", BadgeVariant.ACTIVE)
    }
}

@Composable
private fun AccountEditorCard(
    state: AccountEditorState,
    people: List<PersonListEntry>?,
    peopleError: String?,
    onRetryPeople: () -> Unit,
    onSave: () -> Unit,
    onDiscard: () -> Unit,
    onDelete: () -> Unit,
    onReload: () -> Unit,
) {
    val form = state.form
    val loaded = state.loaded
    val self = loaded?.self == true

    Form(attrs = {
        attr("novalidate", "")
        onSubmit { event ->
            event.preventDefault()
            onSave()
        }
    }) {
        AdminCard(
            title = if (state.isNew) "Neues Konto" else state.original.email,
            id = "konto",
            header = { loaded?.let { StatusBadge(it.draft.enabled, it.passwordSet) } },
            footer = if (loaded == null) {
                null
            } else {
                {
                    AdminButton(variant = ButtonVariant.DANGER, disabled = state.saving || self, onClick = onDelete) { Text("Löschen") }
                    AdminCardFooterSpacer()
                }
            },
            saveActions = {
                AdminButton(disabled = state.saving || (!state.hasChanges && !state.isNew), onClick = onDiscard) { Text("Verwerfen") }
                AdminButton(
                    variant = ButtonVariant.PRIMARY,
                    type = ButtonType.Submit,
                    disabled = state.saving || (!state.hasChanges && !state.isNew),
                ) { Text(if (state.saving) "Speichert …" else if (state.isNew) "Anlegen" else "Speichern") }
            },
        ) {
            if (state.conflict) {
                AdminConflictCallout(onReload = onReload)
            }
            state.error?.let {
                P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }
            if (self) {
                AdminCallout { Text("Das ist dein eigenes Konto: Rolle, Deaktivieren und Löschen gehen nur durch eine andere Person mit Superuser-Rolle.") }
            }

            Fieldset({
                classes(fieldset.block)
                if (state.saving) {
                    attr("disabled", "")
                }
            }) {
                AdminTextField(
                    id = "konto-${AccountDraft.EMAIL}",
                    label = "E-Mail-Adresse",
                    value = form.email,
                    onValueChange = { state.form = state.form.copy(email = it) },
                    type = InputType.Email,
                    error = state.errorFor(AccountDraft.EMAIL),
                    hint = "Damit meldet sich die Person an.",
                    autocomplete = "off",
                )
                RoleSelect(form.role, disabled = self, error = state.errorFor(AccountDraft.ROLE)) {
                    state.form = state.form.copy(role = it)
                }
                AdminPersonPicker(
                    id = "konto-${AccountDraft.PERSON}",
                    personId = form.personId,
                    people = people,
                    onChange = { state.form = state.form.copy(personId = it) },
                    hint = "Neuigkeiten „unter meinem Namen“ zeigen den Namen dieser Person.",
                    error = state.errorFor(AccountDraft.PERSON),
                    fallbackName = loaded?.takeIf { it.draft.personId == form.personId }?.personName,
                    loadError = peopleError,
                    onRetry = onRetryPeople,
                    optional = true,
                )
            }
            if (state.isNew) {
                AdminCallout { Text("Nach dem Anlegen bekommst du einen Link, mit dem die Person ihr Passwort selbst festlegt.") }
            }
        }
    }
}

@Composable
private fun RoleSelect(current: AccountRole?, disabled: Boolean, error: String?, onSelect: (AccountRole?) -> Unit) {
    val id = "konto-${AccountDraft.ROLE}"
    val messageId = "$id-message"
    Div({ classes(field.block) }) {
        Label(forId = id, attrs = { classes(field.element("label").className) }) { Text("Rolle") }
        Select({
            classes(input.block)
            id(id)
            attr("aria-describedby", messageId)
            if (error != null) {
                attr("aria-invalid", "true")
            }
            if (disabled) {
                attr("disabled", "")
            }
            onChange { event -> onSelect(AccountRole.entries.firstOrNull { it.name == event.value }) }
        }) {
            if (current == null) {
                Option("", { selected() }) { Text("Bitte auswählen") }
            }
            AccountRole.entries.forEach { role ->
                Option(role.name, { if (role == current) selected() }) { Text(role.label) }
            }
        }
        Span({
            classes(field.element(if (error != null) "error" else "hint").className)
            id(messageId)
        }) { Text(error ?: current?.description ?: "Admins bearbeiten Inhalte, Superuser auch die Konten.") }
    }
}

/** The password state and the one-time link, shown once with a copy button. */
@Composable
private fun SignInCard(
    state: AccountEditorState,
    loaded: AccountEditor,
    onCreateLink: () -> Unit,
    onLinkDone: () -> Unit,
    onToggleEnabled: () -> Unit,
) {
    var copied by remember(state.link) { mutableStateOf<Boolean?>(null) }
    val scope = rememberCoroutineScope()

    AdminCard(
        title = "Anmeldung",
        id = "konto-anmeldung",
        footer = {
            // applies to the saved account, so not while the form has changes
            AdminButton(disabled = state.saving || loaded.self || state.hasChanges, onClick = onToggleEnabled) {
                Text(if (loaded.draft.enabled) "Deaktivieren" else "Aktivieren")
            }
            AdminCardFooterSpacer()
            AdminButton(disabled = state.saving || !loaded.draft.enabled || state.hasChanges, onClick = onCreateLink) { Text("Link erzeugen") }
        },
    ) {
        P {
            Text(
                when {
                    loaded.passwordSet -> "Das Passwort ist festgelegt. Ein neuer Link setzt es zurück; bis er verwendet wird, gilt das alte."
                    else -> "Noch kein Passwort: Die Person legt es mit dem Link selbst fest."
                },
            )
        }
        loaded.linkExpiresAt?.takeIf { state.link == null }?.let { expiresAt ->
            P({ classes(card.element("hint").className) }) { Text("Ein Link ist offen, gültig bis ${formatDateTime(expiresAt)}.") }
        }
        if (!loaded.draft.enabled) {
            P({ classes(card.element("hint").className) }) { Text("Für deaktivierte Konten gibt es keinen Link.") }
        }

        state.link?.let { link ->
            AdminCallout(alert = true) {
                Text("Dieser Link ist nur jetzt sichtbar. Kopiere ihn und gib ihn der Person weiter. Er gilt einmal, bis ${formatDateTime(link.expiresAt)}.")
            }
            // the design system's CopyField
            Div({ classes(copyField.block) }) {
                Label(forId = "konto-link", attrs = { classes(copyField.element("label").className) }) { Text("Link zum Passwort festlegen") }
                Div({ classes(copyField.element("row").className) }) {
                    Input(InputType.Text) {
                        classes(input.block, copyField.element("input").className)
                        id("konto-link")
                        value(link.url)
                        readOnly()
                        attr("aria-describedby", "konto-link-status")
                        onFocus { event -> event.target.asDynamic().select() }
                    }
                    AdminButton(onClick = {
                        scope.launch {
                            copied = runCatching { window.navigator.clipboard.writeText(link.url).await() }.isSuccess
                        }
                    }) { Text("Kopieren") }
                }
                Span({
                    classes(copyField.element("status").className)
                    id("konto-link-status")
                    attr("role", "status")
                }) {
                    when (copied) {
                        true -> Text("Kopiert.")
                        false -> Text("Kopieren hat nicht geklappt. Bitte markiere den Link und kopiere ihn selbst.")
                        null -> Unit
                    }
                }
                Div {
                    AdminButton(variant = ButtonVariant.LINK, onClick = onLinkDone) { Text("Erledigt, Link ausblenden") }
                }
            }
        }
    }
}

/** Whether this link is still the account's open one: a newer link, e.g. from another superuser, or disabling ends it. */
private fun PasswordLink.isOpenFor(account: AccountEditor): Boolean =
    account.draft.enabled && account.linkExpiresAt == expiresAt
